<template>
  <div class="container-fluid">

    <!-- Cargando -->
    <div v-if="loading" class="text-center py-5">
      <div class="loader-box">
        <div class="loader-3"></div>
      </div>
      <p>Cargando documento...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center py-5">
      <div class="alert alert-danger">
        <h5>Error al cargar el documento</h5>
        <p>{{ error.message || 'Ocurrió un error inesperado' }}</p>
        <button class="btn btn-secondary me-2" @click="$router.back()">
          <i class="fa fa-arrow-left"></i> Regresar
        </button>
        <button class="btn btn-primary" @click="fetchDocumento()">Reintentar</button>
      </div>
    </div>

    <!-- Contenido -->
    <div v-else-if="documento">

      <!-- Encabezado -->
      <div class="page-title">
        <div class="row align-items-center">
          <div class="col-12 d-flex align-items-center gap-3">
            <button class="btn btn-outline-secondary btn-sm" @click="$router.back()">
              <i class="fa fa-arrow-left"></i> Regresar
            </button>
            <i :class="[getDocumentIcon(documento.meta?.download_url), 'fa-2x']"></i>
            <h3 class="mb-0">{{ documento.title || 'Sin título' }}</h3>
          </div>
        </div>
      </div>

      <div class="row">

        <!-- Panel de detalles -->
        <div class="col-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0"><i class="fa fa-info-circle me-2"></i>Detalles del documento</h5>
              <a v-if="downloadUrl" :href="downloadUrl" target="_blank" class="btn btn-primary btn-sm">
                <i class="fa fa-download me-1"></i>Descargar
              </a>
            </div>
            <div class="card-body">
              <div class="row g-3">

                <!-- Título -->
                <div class="col-md-4">
                  <p class="text-muted mb-1 small">Título</p>
                  <p class="fw-semibold mb-0">{{ documento.title || '—' }}</p>
                </div>

                <!-- Descripción -->
                <div class="col-md-8" v-if="documento.descripcion">
                  <p class="text-muted mb-1 small">Descripción</p>
                  <p class="mb-0">{{ documento.descripcion }}</p>
                </div>

                <!-- Origen -->
                <div class="col-md-3" v-if="documento.origen">
                  <p class="text-muted mb-1 small">Origen</p>
                  <p class="mb-0">{{ documento.origen }}</p>
                </div>

                <!-- Tipo -->
                <div class="col-md-2" v-if="documento.meta?.document_type">
                  <p class="text-muted mb-1 small">Tipo</p>
                  <p class="mb-0">{{ documento.meta.document_type }}</p>
                </div>

                <!-- Idioma -->
                <div class="col-md-2" v-if="documento.meta?.locale">
                  <p class="text-muted mb-1 small">Idioma</p>
                  <p class="mb-0">{{ documento.meta.locale }}</p>
                </div>

                <!-- Publicado -->
                <div class="col-md-2" v-if="firstPublishedAt">
                  <p class="text-muted mb-1 small">Publicado</p>
                  <p class="mb-0">{{ firstPublishedAt }}</p>
                </div>

                <!-- Actualizado -->
                <div class="col-md-2" v-if="lastPublishedAt">
                  <p class="text-muted mb-1 small">Actualizado</p>
                  <p class="mb-0">{{ lastPublishedAt }}</p>
                </div>

                <!-- Formato -->
                <div class="col-md-1" v-if="fileExtension">
                  <p class="text-muted mb-1 small">Formato</p>
                  <span class="badge bg-secondary">{{ fileExtension }}</span>
                </div>

                <!-- Tamaño -->
                <div class="col-md-2" v-if="documento.meta?.file_size">
                  <p class="text-muted mb-1 small">Tamaño</p>
                  <p class="mb-0">{{ formatFileSize(documento.meta.file_size) }}</p>
                </div>

                <!-- Etiquetas -->
                <div class="col-12" v-if="documento.meta?.tags?.length">
                  <p class="text-muted mb-1 small">Etiquetas</p>
                  <span
                    v-for="tag in documento.meta.tags"
                    :key="tag"
                    class="badge bg-primary me-1"
                  >{{ tag }}</span>
                </div>

                <!-- Campos extra del meta -->
                <template v-if="extraMeta.length">
                  <div
                    v-for="field in extraMeta"
                    :key="field.key"
                    class="col-md-3"
                  >
                    <p class="text-muted mb-1 small text-capitalize">{{ field.label }}</p>
                    <p class="mb-0">{{ field.value }}</p>
                  </div>
                </template>

              </div>
            </div>
          </div>
        </div>

        <!-- Panel de previsualización -->
        <div class="col-12">
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-eye me-2"></i>Previsualización</h5>
            </div>
            <div class="card-body p-0" style="min-height: 600px;">

              <!-- PDF -->
              <iframe
                v-if="isPdf"
                :src="pdfViewerUrl"
                style="width: 100%; height: 650px; border: none;"
                title="Previsualización del documento"
              ></iframe>

              <!-- Imagen -->
              <div v-else-if="isImage" class="text-center p-3">
                <img :src="downloadUrl" class="img-fluid rounded" alt="Previsualización" />
              </div>

              <!-- Sin previsualización -->
              <div v-else class="d-flex flex-column align-items-center justify-content-center h-100 py-5 text-muted">
                <i :class="[getDocumentIcon(documento.meta?.download_url)]" style="font-size: 5rem;"></i>
                <p class="mt-3">No hay previsualización disponible para este tipo de archivo.</p>
                <a v-if="downloadUrl" :href="downloadUrl" target="_blank" class="btn btn-outline-primary mt-2">
                  <i class="fa fa-external-link me-1"></i> Abrir en nueva pestaña
                </a>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import axios from "@/axios";

const KNOWN_META_KEYS = ["download_url", "tags", "document_type", "locale", "file_size", "search_description", "origin"];

export default {
  name: "documentoDetalle",
  data() {
    return {
      documento: null,
      loading: true,
      error: null,
    };
  },
  created() {
    this.fetchDocumento();
  },
  computed: {
    downloadUrl() {
      const url = this.documento?.meta?.download_url;
      if (!url) return null;
      return url.replace("http://localhost", "https://rverdeadmin.klatus.systems");
    },
    fileExtension() {
      if (!this.downloadUrl) return null;
      const match = this.downloadUrl.match(/\.([a-zA-Z0-9]+)(\?|$)/);
      return match ? match[1].toUpperCase() : null;
    },
    isPdf() {
      return this.fileExtension === "PDF";
    },
    pdfViewerUrl() {
      if (!this.downloadUrl) return null;
      return `https://docs.google.com/viewer?url=${encodeURIComponent(this.downloadUrl)}&embedded=true`;
    },
    isImage() {
      return ["JPG", "JPEG", "PNG", "GIF", "WEBP", "SVG"].includes(this.fileExtension);
    },
    firstPublishedAt() {
      const d = this.documento?.meta?.first_published_at;
      return d ? new Date(d).toLocaleDateString("es-MX", { year: "numeric", month: "long", day: "numeric" }) : null;
    },
    lastPublishedAt() {
      const d = this.documento?.meta?.last_published_at;
      return d ? new Date(d).toLocaleDateString("es-MX", { year: "numeric", month: "long", day: "numeric" }) : null;
    },
    extraMeta() {
      const meta = this.documento?.meta || {};
      const skip = new Set([...KNOWN_META_KEYS, "first_published_at", "last_published_at", "seo_title", "search_description", "slug", "html_url", "type", "detail_url"]);
      return Object.entries(meta)
        .filter(([k, v]) => !skip.has(k) && v !== null && v !== "" && !Array.isArray(v))
        .map(([k, v]) => ({ key: k, label: k.replace(/_/g, " "), value: v }));
    },
  },
  methods: {
    async fetchDocumento() {
      this.loading = true;
      this.error = null;
      try {
        const { id } = this.$route.params;
        const response = await axios.get(`documents/${id}`);
        this.documento = response.data;
      } catch (err) {
        this.error = err;
        console.error("Error al cargar documento:", err);
      } finally {
        this.loading = false;
      }
    },
    getDocumentIcon(url) {
      if (!url) return "fa fa-file text-secondary";
      const extension = url.split(".").pop().toLowerCase();
      const icons = {
        pdf: "fa fa-file-pdf-o text-danger",
        doc: "fa fa-file-word-o text-primary",
        docx: "fa fa-file-word-o text-primary",
        xls: "fa fa-file-excel-o text-success",
        xlsx: "fa fa-file-excel-o text-success",
        ppt: "fa fa-file-powerpoint-o text-warning",
        pptx: "fa fa-file-powerpoint-o text-warning",
        jpg: "fa fa-file-image-o text-info",
        jpeg: "fa fa-file-image-o text-info",
        png: "fa fa-file-image-o text-info",
        zip: "fa fa-file-archive-o text-secondary",
        rar: "fa fa-file-archive-o text-secondary",
        txt: "fa fa-file-text-o text-muted",
      };
      return icons[extension] || "fa fa-file-o text-secondary";
    },
    formatFileSize(bytes) {
      if (!bytes) return "—";
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
  },
};
</script>
